import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Stack, Typography } from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import {
  parseAgentQuestionAnswersFromMessage,
  ParsedAgentQuestionAnswer,
} from '../utils/agentQuestions';

type DisplayAnswer = {
  key: string;
  text: string;
};

const getQuestionKey = (item: ParsedAgentQuestionAnswer): string =>
  [
    item.question,
    item.answerOption?.value ?? 'none',
    (item.answerOptions ?? []).map(option => option.value).join('|') || 'none',
    item.answer ?? '',
  ].join('::');

const collectDisplayAnswers = (
  item: ParsedAgentQuestionAnswer,
  notSpecifiedLabel: string
): DisplayAnswer[] => {
  const answers: DisplayAnswer[] = [];

  if (item.answerOption?.label) {
    answers.push({
      key: `single:${item.answerOption.value}`,
      text: item.answerOption.label,
    });
  }

  if (item.answerOptions && item.answerOptions.length > 0) {
    answers.push({
      key: `multi:${item.answerOptions.map(option => option.value).join('|')}`,
      text: item.answerOptions.map(option => option.label).join(', '),
    });
  }

  if (item.answer) {
    answers.push({ key: `custom:${item.answer}`, text: item.answer });
  }

  return answers.length > 0
    ? answers
    : [{ key: 'empty', text: notSpecifiedLabel }];
};

export const ChatMessageText = ({ text }: { text: string }) => {
  const intl = useIntl();
  const notSpecifiedLabel = intl.formatMessage({
    id: 'features.chat.message.notSpecified',
  });
  const parsedAgentAnswers = useMemo(
    () => parseAgentQuestionAnswersFromMessage(text),
    [text]
  );

  return parsedAgentAnswers ? (
    <Stack spacing={1}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          letterSpacing: 0.2,
          textTransform: 'uppercase',
          opacity: 0.9,
        }}
      >
        {intl.formatMessage({ id: 'features.chat.message.answersSent' })}
      </Typography>
      {parsedAgentAnswers.map(item => (
        <Stack
          key={getQuestionKey(item)}
          spacing={0.4}
          sx={{
            px: 1,
            py: 0.8,
            borderRadius: borderRadius('s'),
            bgcolor: 'rgba(255, 255, 255, 0.16)',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {item.question}
          </Typography>
          {collectDisplayAnswers(item, notSpecifiedLabel).map(answer => (
            <Typography key={answer.key} variant="body2">
              {answer.text}
            </Typography>
          ))}
        </Stack>
      ))}
    </Stack>
  ) : (
    <Typography
      variant="body2"
      sx={{
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {text}
    </Typography>
  );
};
